import { useState, useMemo } from "react";
import { ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import type { SalesFunnel } from "@/types/lead";
import { resolveFunnelDisplayName } from "@/lib/funnel-display-label";
import type { WorkspaceIntegrationsMap } from "@/hooks/useWorkspaceIntegrationsMap";

interface MoveLeadsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  funnels: SalesFunnel[];
  workspaceId: string;
  onComplete?: () => void;
  integrationById?: WorkspaceIntegrationsMap;
}

export function MoveLeadsDialog({
  open,
  onOpenChange,
  funnels,
  workspaceId,
  onComplete,
  integrationById,
}: MoveLeadsDialogProps) {
  const { toast } = useToast();
  const [sourceFunnelId, setSourceFunnelId] = useState<string>("");
  const [targetFunnelId, setTargetFunnelId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [isBatchRunning, setIsBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [leadCount, setLeadCount] = useState<number>(0);

  const sourceFunnel = useMemo(
    () => funnels.find((f) => f.id === sourceFunnelId),
    [funnels, sourceFunnelId]
  );
  const targetFunnel = useMemo(
    () => funnels.find((f) => f.id === targetFunnelId),
    [funnels, targetFunnelId]
  );

  const availableTargets = useMemo(
    () => funnels.filter((f) => f.id !== sourceFunnelId),
    [funnels, sourceFunnelId]
  );

  const handlePreview = async () => {
    if (!sourceFunnelId || !targetFunnelId) return;

    setIsLoading(true);
    try {
      const { data: count, error } = await (supabase as any).rpc("count_leads_in_funnel", {
        p_workspace_id: workspaceId,
        p_funnel_id: sourceFunnelId,
      });

      if (error) throw error;

      setLeadCount(Number(count) || 0);
      setConfirmOpen(true);
    } catch (error: any) {
      toast({
        title: "Erro ao contar leads",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmMove = async () => {
    setIsLoading(true);
    setIsBatchRunning(true);
    setBatchProgress(0);
    try {
      // Get the first stage of the target funnel
      const { data: targetStages, error: stagesError } = await supabase
        .from("lead_stages")
        .select("slug")
        .eq("funnel_id", targetFunnelId)
        .order("order_index", { ascending: true })
        .limit(1);

      if (stagesError) throw stagesError;

      const firstStage = targetStages?.[0]?.slug || "novo";

      let cursorId: string | null = null;
      let updatedTotal = 0;
      const maxIterations = 2000;

      for (let i = 0; i < maxIterations; i++) {
        const { data, error: fnError } = await supabase.functions.invoke("bulk-move-leads-funnel", {
          body: {
            workspaceId,
            sourceFunnelId,
            targetFunnelId,
            targetStage: firstStage,
            cursorId,
            batchSize: 2000,
          },
        });

        if (fnError) throw fnError;

        const updated = Number((data as any)?.updated || 0);
        updatedTotal += updated;
        setBatchProgress(updatedTotal);

        const done = Boolean((data as any)?.done);
        cursorId = ((data as any)?.nextCursor as string | null) || null;
        if (done || updated === 0 || !cursorId) break;
      }

      toast({
        title: "Leads movidos com sucesso",
        description: `${updatedTotal || leadCount} leads foram movidos para o funil "${targetFunnel?.name}".`,
      });

      setConfirmOpen(false);
      onOpenChange(false);
      setSourceFunnelId("");
      setTargetFunnelId("");
      onComplete?.();
    } catch (error: any) {
      toast({
        title: "Erro ao mover leads",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setIsBatchRunning(false);
    }
  };

  const handleClose = (nextOpen?: boolean) => {
    if (nextOpen === true) {
      onOpenChange(true);
      return;
    }
    if (nextOpen === false && (isLoading || isBatchRunning)) return;
    setSourceFunnelId("");
    setTargetFunnelId("");
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Mover Leads Entre Funis</DialogTitle>
            <DialogDescription>
              Mova todos os leads de um funil para outro. Os leads serão colocados no primeiro estágio do funil de destino.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="source-funnel">Funil de Origem</Label>
              <Select value={sourceFunnelId} onValueChange={setSourceFunnelId}>
                <SelectTrigger id="source-funnel">
                  <SelectValue placeholder="Selecione o funil de origem" />
                </SelectTrigger>
                <SelectContent>
                  {funnels.map((funnel) => (
                    <SelectItem key={funnel.id} value={funnel.id}>
                      <div className="flex items-center gap-2">
                        <div className={cn("w-3 h-3 rounded-full", funnel.color)} />
                        {resolveFunnelDisplayName(funnel, integrationById)}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {sourceFunnelId && (
              <div className="flex justify-center py-2">
                <ArrowRight className="w-5 h-5 text-muted-foreground" />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="target-funnel">Funil de Destino</Label>
              <Select
                value={targetFunnelId}
                onValueChange={setTargetFunnelId}
                disabled={!sourceFunnelId}
              >
                <SelectTrigger id="target-funnel">
                  <SelectValue placeholder="Selecione o funil de destino" />
                </SelectTrigger>
                <SelectContent>
                  {availableTargets.map((funnel) => (
                    <SelectItem key={funnel.id} value={funnel.id}>
                      <div className="flex items-center gap-2">
                        <div className={cn("w-3 h-3 rounded-full", funnel.color)} />
                        {resolveFunnelDisplayName(funnel, integrationById)}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                onClick={handlePreview}
                disabled={!sourceFunnelId || !targetFunnelId || isLoading || isBatchRunning}
                className="flex-1"
              >
                {isLoading ? "Verificando..." : "Mover Leads"}
              </Button>
              <Button
                variant="outline"
                onClick={() => handleClose(false)}
                className="flex-1"
                disabled={isLoading || isBatchRunning}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && (isLoading || isBatchRunning)) return;
          setConfirmOpen(nextOpen);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Movimentação</AlertDialogTitle>
            <AlertDialogDescription>
              {leadCount === 0 ? (
                <>Não há leads no funil "{sourceFunnel?.name}".</>
              ) : (
                <>
                  Você está prestes a mover <strong>{leadCount}</strong> lead{leadCount !== 1 && "s"} do funil "<strong>{sourceFunnel?.name}</strong>" para o funil "<strong>{targetFunnel?.name}</strong>".
                  <br /><br />
                  Todos os leads serão movidos para o primeiro estágio do funil de destino. Esta ação não pode ser desfeita.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading || isBatchRunning}>Cancelar</AlertDialogCancel>
            {leadCount > 0 && (
              <AlertDialogAction onClick={handleConfirmMove} disabled={isLoading || isBatchRunning}>
                {isBatchRunning ? `Movendo... (${batchProgress})` : isLoading ? "Movendo..." : "Confirmar"}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
